import { getLatestDicomEditJob } from '@/lib/api';
import { invalidateStudyContent } from '@/lib/viewer/studyContentQueries';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
/** Poll cadence while a DICOM edit is queued or running. */
export const DICOM_EDIT_JOB_POLL_MS = 2_000;
export const dicomEditJobQueryKey = (studyId) => ['study-dicom-edit-job', studyId];
/**
 * Jobs this client submitted whose completion has not refreshed the study yet. Module-level so
 * it survives the editor dialog closing and a row remounting, and so a job that is already
 * `completed` on its first fetch (a small study) still triggers the refresh.
 */
const submittedJobs = new Set();
export function markDicomEditSubmitted(jobId) {
    submittedJobs.add(jobId);
}
export function isDicomEditActive(job) {
    return job?.state === 'queued' || job?.state === 'processing';
}
/**
 * The study's newest DICOM tag edit (Edit DICOM tags).
 * Polls while it is queued or running and stops once it settles. When a job
 * this hook watched finishes, the study's content queries are invalidated so
 * the viewer picks up the rewritten files: each one moved to a new storage key,
 * so fresh instance URLs are fresh bytes.
 */
export function useLatestDicomEditJob(studyId, enabled) {
    const queryClient = useQueryClient();
    const query = useQuery({
        queryKey: dicomEditJobQueryKey(studyId),
        queryFn: () => getLatestDicomEditJob(studyId),
        enabled,
        staleTime: 30_000,
        refetchInterval: (q) => (isDicomEditActive(q.state.data) ? DICOM_EDIT_JOB_POLL_MS : false),
        refetchIntervalInBackground: false,
    });
    // Invalidate once per job when it completes: either this hook saw it go active, or this
    // client submitted it (so even a first fetch that is already `completed` counts). A job that
    // was already complete when the row first loaded, and was neither, is left alone.
    const watching = useRef(null);
    const job = query.data;
    useEffect(() => {
        if (!job)
            return;
        if (isDicomEditActive(job)) {
            watching.current = job.jobId;
            return;
        }
        if (job.state === 'completed' &&
            (watching.current === job.jobId || submittedJobs.has(job.jobId))) {
            watching.current = null;
            submittedJobs.delete(job.jobId);
            void invalidateStudyContent(queryClient, studyId);
        }
    }, [job, queryClient, studyId]);
    return query;
}
